import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { OCC_CART_ID_CURRENT, StateWithProcess } from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ActiveCartFacade } from '../../../features/cart/base/facades';
import { UserIdService } from '../../auth/user-auth/facade';
import { CartType, LoaderError, PunchOutData } from '../../model';
import {
  getProcessErrorFactory,
  getProcessLoadingFactory,
  getProcessStateFactory,
  getProcessSuccessFactory,
} from '../../process/store/selectors/process.selectors';
import { PunchOutConnector } from '../connectors/punch-out-connector';
import { PunchOutFacade } from '../facades';
import { PunchOut } from '../store/actions/punch-out-action';
import { LoadPunchOutData, LoadPunchOutDataReset } from '../store/actions/punch-out-data-action';
import { PUNCHOUT_DATA_PROCESS, PUNCHOUT_PROCESS } from '../store/user-state';

@Injectable()
export class PunchOutService implements PunchOutFacade {
  constructor(
    private activeCartService: ActiveCartFacade,
    private userIdService: UserIdService,
    @Inject(DOCUMENT) private readonly document: Document,
    private store: Store<StateWithProcess<PunchOutData>>,
    private connector: PunchOutConnector
  ) {}

  /** The Window object from Document defaultView */
  private get window(): Window {
    return this.document.defaultView;
  }

  loadPunchOutData(): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new LoadPunchOutData(userId)),
      () => {}
    );
  }

  getPunchOutEntity(): Observable<PunchOutData> {
    return this.store.pipe(
      select(getProcessStateFactory(PUNCHOUT_DATA_PROCESS)),
      map((loaderState) => loaderState.value)
    );
  }

  punchOut(cartId: string): void {
    this.userIdService.takeUserId(true).subscribe((userId) => this.store.dispatch(new PunchOut(cartId, userId)));
  }

  cancelPunchOut(): Observable<any> {
    return combineLatest([this.activeCartService.getActiveCartId(CartType.stock), this.userIdService.getUserId()]).pipe(
      take(1),
      switchMap(([cartId, userId]) => {
        if (cartId && cartId !== OCC_CART_ID_CURRENT) {
          return this.connector.cancel(cartId, userId);
        }
        return of({});
      })
    );
  }

  /**
   * Clears PunchOut data and redirects if data contains a cancel property
   *
   * @param punchOutData
   */
  logOut(punchOutData: PunchOutData) {
    if (punchOutData && punchOutData['cancel']) {
      this.window.location.replace(punchOutData['cancel']);
    }
  }

  punchOutReset(): void {
    this.store.dispatch(new LoadPunchOutDataReset());
  }

  punchOutLoading(): Observable<boolean> {
    return this.store.select(getProcessLoadingFactory(PUNCHOUT_PROCESS));
  }

  punchOutError(): Observable<LoaderError | undefined> {
    return this.store.select(getProcessErrorFactory(PUNCHOUT_PROCESS));
  }

  punchOutSuccess(): Observable<boolean> {
    return this.store.select(getProcessSuccessFactory(PUNCHOUT_PROCESS));
  }
}
